// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../../assets/images/logo.png";
import productsData from "../../data/productsData";
import categoriesData from "../../data/categoriesData";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import ThemeSwitcher from "../ThemeSwitcher/ThemeSwitcher";
import "./Navbar.css";

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuState, setMobileMenuState] = useState("main"); // "main" | "products"

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setMobileMenuState("main");
  }, [location]);

  const categories = useMemo(() => {
    return [...new Set(productsData.map(p => p.category).filter(Boolean).map(c => c.trim()).filter(c => c.length > 0))];
  }, []);

  const manufacturers = useMemo(() => {
    return [...new Set(productsData.map(p => p.distributor).filter(Boolean).map(m => m.trim()).filter(m => m.length > 0))];
  }, []);

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.append("query", searchTerm.trim());
    if (selectedCategory) params.append("category", selectedCategory);
    if (selectedManufacturer) params.append("manufacturer", selectedManufacturer);
    if (selectedSubCategory) params.append("subCategory", selectedSubCategory);
    if (selectedSubSubCategory) params.append("subSubCategory", selectedSubSubCategory);
    navigate(`/products?${params.toString()}`);
  }, [searchTerm, selectedCategory, selectedManufacturer, selectedSubCategory, selectedSubSubCategory, navigate]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  useEffect(() => {
    setSelectedSubCategory("");
    setSelectedSubSubCategory("");
  }, [selectedCategory]);

  const categoryObject = useMemo(
    () => categoriesData.find((cat) => cat.name === selectedCategory),
    [selectedCategory]
  );

  return (
    <header>
      <nav className="navbar" role="navigation" aria-label="Main Navigation">
        <div className="navbar-top">
          <div className="navbar-wrapper">
            <div className="navbar-left">
              <Link to="/">
                <img src={logo} alt={t("navbar.home", "Home")} className="logo" />
              </Link>
            </div>

            <div className="navbar-center">
              <ul className="nav-links">
                <li><Link to="/">{t("navbar.home")}</Link></li>
                <li><Link to="/manufacturers">{t("navbar.manufacturers")}</Link></li>
                <li><Link to="/products">{t("navbar.products")}</Link></li>
                <li><Link to="/about">{t("navbar.aboutUs")}</Link></li>
                <li><Link to="/contact">{t("navbar.contactUs")}</Link></li>
              </ul>
            </div>

            <div className="navbar-right">
              <LanguageSwitcher />
              <ThemeSwitcher />
              <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
                {menuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>

          {menuOpen && (
            <div className="navbar-mobile">
              <div className={`mobile-menu-container ${mobileMenuState}`}>
                {/* Main Menu View */}
                <div className="mobile-menu-view main-view">
                  <Link to="/" onClick={() => setMenuOpen(false)}>{t("navbar.home")}</Link>
                  <Link to="/manufacturers" onClick={() => setMenuOpen(false)}>{t("navbar.manufacturers")}</Link>
                  
                  <div className="mobile-nav-item" onClick={() => setMobileMenuState("products")}>
                    <span>{t("navbar.products")}</span>
                    <span className="arrow-right">›</span>
                  </div>

                  <Link to="/about" onClick={() => setMenuOpen(false)}>{t("navbar.aboutUs")}</Link>
                  <Link to="/contact" onClick={() => setMenuOpen(false)}>{t("navbar.contactUs")}</Link>
                </div>

                {/* Products Sub-Menu View */}
                <div className="mobile-menu-view products-view">
                  <div className="mobile-menu-header" onClick={() => setMobileMenuState("main")}>
                    <span className="arrow-left">‹</span>
                    <span>Back to Menu</span>
                  </div>
                  
                  <div className="mobile-menu-content">
                    <Link to="/products" onClick={() => setMenuOpen(false)} className="view-all">View All Products</Link>
                    {categories.map((cat, idx) => (
                      <Link 
                        key={idx} 
                        to={`/products?category=${encodeURIComponent(cat)}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {cat}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="navbar-search">
          <input
            type="text"
            placeholder={t("search.placeholder", "Search products...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="filterSelect">
            <option value="">{t("search.allCategories", "All Categories")}</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>{cat}</option>
            ))}
          </select>

          <select value={selectedManufacturer} onChange={(e) => setSelectedManufacturer(e.target.value)} className="filterSelect">
            <option value="">{t("search.allManufacturers", "All Manufacturers")}</option>
            {manufacturers.map((manu, idx) => (
              <option key={idx} value={manu}>{manu}</option>
            ))}
          </select>

          {selectedCategory && categoryObject?.subcategories && (
            <select value={selectedSubCategory} onChange={(e) => setSelectedSubCategory(e.target.value)} className="filterSelect">
              <option value="">All Subcategories</option>
              {categoryObject.subcategories.map((sub, idx) => (
                <option key={idx} value={typeof sub === 'object' ? sub.name : sub}>{typeof sub === 'object' ? sub.name : sub}</option>
              ))}
            </select>
          )}

          {selectedSubCategory && (() => {
            const subObj = categoryObject.subcategories.find(s => typeof s === 'object' && s.name === selectedSubCategory);
            if (subObj?.subsubcategories?.length > 0) {
              return (
                <select value={selectedSubSubCategory} onChange={(e) => setSelectedSubSubCategory(e.target.value)} className="filterSelect">
                  <option value="">All Sub-Subcategories</option>
                  {subObj.subsubcategories.map((ss, idx2) => (
                    <option key={idx2} value={ss}>{ss}</option>
                  ))}
                </select>
              );
            }
            return null;
          })()}

          <button onClick={handleSearch}>{t("search.button", "Search")}</button>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
